// src/components/common/ArrivalNotifyForm.jsx
// #103 zero-inventory graceful degradation — anonymous "get notified when
// new animals arrive" email capture. POSTs to the public
// /api/store/arrivals/subscribe/ endpoint (no account needed); success
// swaps the form for a quiet thank-you line, errors render inline.
// Reused by: HomePage (zero-inventory hero + lean weeks), the /frogs
// learn-mode species cards (per-species interest), and the sold-out PDP.
import React, { useId, useState } from 'react';
import { subscribeArrivals } from '../../services/api';
import '../../styles/arrivalNotify.css';

// Light client-side shape check only — the server does the real
// validation (Django validate_email) and 400s with a message.
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ArrivalNotifyForm = ({
  speciesId = null,
  compact = false,
  prompt = 'Get notified when new animals arrive',
  className = '',
}) => {
  const inputId = useId();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!EMAIL_SHAPE.test(value)) {
      setError('Please enter a valid email address.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await subscribeArrivals(value, speciesId);
      // already_subscribed is deliberately NOT surfaced — the repeat
      // subscriber gets the same quiet thank-you, not an error.
      setDone(true);
    } catch (err) {
      const status = err?.response?.status;
      setError(
        status === 429
          ? 'Too many attempts right now — please try again later.'
          : err?.response?.data?.error
            || 'Something went wrong — please try again.'
      );
    } finally {
      setBusy(false);
    }
  };

  const rootClass = `ff-arrival-notify${compact ? ' ff-arrival-notify--compact' : ''}${className ? ` ${className}` : ''}`;

  if (done) {
    return (
      <p className={`${rootClass} ff-arrival-notify-done`} role="status">
        Thanks — we&rsquo;ll email you when new animals arrive.
      </p>
    );
  }

  return (
    <form className={rootClass} onSubmit={handleSubmit} noValidate>
      <label className="ff-arrival-notify-prompt" htmlFor={inputId}>
        {prompt}
      </label>
      <div className="ff-arrival-notify-row">
        <input
          id={inputId}
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setError(null); }}
          className="ff-arrival-notify-input"
          disabled={busy}
        />
        <button
          type="submit"
          className={`ff-btn ff-btn--secondary${compact ? ' ff-btn--sm' : ''}`}
          disabled={busy}
        >
          {busy ? 'Subscribing…' : 'Notify me'}
        </button>
      </div>
      {error && (
        <p className="ff-arrival-notify-error" role="alert">{error}</p>
      )}
    </form>
  );
};

export default ArrivalNotifyForm;
